'use client';

import { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { useAddressStore } from '@/stores/address-store';
import {
  loadYandexMaps,
  type YandexMapsAPI,
  type YandexMapInstance,
  type YandexPlacemark,
  type YandexProjection,
} from '@/lib/yandex-map-loader';

export interface MapClickResult {
  lat: number;
  lon: number;
  address: string;
}

export interface YandexMapHandle {
  /** Viewport coordinates, matching PointerEvent.clientX/clientY. */
  pixelToGeo: (clientX: number, clientY: number) => { lat: number; lon: number } | null;
  placeMarker: (lat: number, lon: number) => void;
  resize: () => void;
}

interface YandexMapProps {
  coordinates?: { lat: number; lon: number };
  zoom?: number;
  className?: string;
  showZones?: boolean;
  active?: boolean;
  onReady?: () => void;
  onError?: () => void;
}

const DEFAULT_CENTER = [47.2357, 39.7015];
const DEFAULT_ZOOM = 12;
const MARKER_OPTIONS = { preset: 'islands#violetDotIcon', iconColor: '#7B42FF' };

export const YandexMap = forwardRef<YandexMapHandle, YandexMapProps>(function YandexMap(
  { coordinates, zoom = DEFAULT_ZOOM, className, showZones = false, active = true, onReady, onError },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<YandexMapInstance | null>(null);
  const markerRef = useRef<YandexPlacemark | null>(null);
  const ymapsRef = useRef<YandexMapsAPI | null>(null);
  const callbacksRef = useRef({ onReady, onError });
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [previousActive, setPreviousActive] = useState(active);
  const [attempt, setAttempt] = useState(0);
  const deliveryPoints = useAddressStore((state) => state.deliveryPoints);
  const latitude = coordinates?.lat;
  const longitude = coordinates?.lon;

  // Reset before effects run when the SDK map is mounted again. Retry resets
  // loading in its click handler; initial mounting already starts in loading.
  if (previousActive !== active) {
    setPreviousActive(active);
    setStatus('loading');
  }

  useEffect(() => {
    callbacksRef.current = { onReady, onError };
  }, [onReady, onError]);

  const resize = useCallback(() => {
    const host = containerRef.current;
    if (!mapRef.current || !host || host.clientWidth === 0 || host.clientHeight === 0) return;
    try { mapRef.current.container.fitToViewport(); } catch { /* The SDK can finish a resize during teardown. */ }
  }, []);

  useImperativeHandle(ref, () => ({
    pixelToGeo(clientX, clientY) {
      const map = mapRef.current;
      const host = containerRef.current;
      if (!map || !host || !Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;
      const rect = host.getBoundingClientRect();
      if (!rect.width || !rect.height || clientX < rect.left || clientX > rect.right ||
        clientY < rect.top || clientY > rect.bottom) return null;

      try {
        const projection = map.options.get('projection') as YandexProjection | undefined;
        if (!projection?.fromGlobalPixels) return null;
        const mapZoom = map.getZoom();
        let pixels: number[];
        if (map.converter?.pageToGlobal) {
          // The SDK expects document pixels; the popup passes viewport pixels.
          pixels = map.converter.pageToGlobal([clientX + window.scrollX, clientY + window.scrollY]);
        } else if (projection.toGlobalPixels) {
          // Keep the SDK's Mercator projection when a converter is unavailable.
          const center = projection.toGlobalPixels(map.getCenter(), mapZoom);
          const size = map.container.getSize();
          pixels = [
            center[0] + ((clientX - rect.left) / rect.width - 0.5) * size[0],
            center[1] + ((clientY - rect.top) / rect.height - 0.5) * size[1],
          ];
        } else {
          return null;
        }
        const [lat, lon] = projection.fromGlobalPixels(pixels, mapZoom);
        return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
      } catch {
        return null;
      }
    },
    placeMarker(lat, lon) {
      const ymaps = ymapsRef.current;
      const map = mapRef.current;
      if (!ymaps || !map || !Number.isFinite(lat) || !Number.isFinite(lon)) return;
      const coords = [lat, lon];
      try {
        if (markerRef.current) {
          markerRef.current.geometry.setCoordinates(coords);
        } else {
          markerRef.current = new ymaps.Placemark(coords, {}, MARKER_OPTIONS);
          map.geoObjects.add(markerRef.current);
        }
        map.setCenter(coords, undefined, { duration: 300 });
      } catch { /* Address entry remains available if the SDK fails. */ }
    },
    resize,
  }), [resize]);

  useEffect(() => {
    if (!active || !containerRef.current) return;
    let cancelled = false;
    let map: YandexMapInstance | null = null;
    let observer: ResizeObserver | null = null;
    let frame: number | undefined;

    const release = () => {
      observer?.disconnect();
      window.removeEventListener('resize', resize);
      if (frame !== undefined) cancelAnimationFrame(frame);
      if (map) {
        try { map.destroy(); } catch { /* Already disposed by the SDK. */ }
      }
      if (mapRef.current === map) {
        mapRef.current = null;
        markerRef.current = null;
        ymapsRef.current = null;
      }
      map = null;
    };

    loadYandexMaps().then((api) => {
      if (cancelled || !containerRef.current) return;
      try {
        map = new api.Map(containerRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          controls: ['zoomControl'],
        });
        ymapsRef.current = api;
        mapRef.current = map;
        if (typeof ResizeObserver !== 'undefined') {
          let previousWidth = -1;
          let previousHeight = -1;
          observer = new ResizeObserver(([entry]) => {
            if (!entry || cancelled) return;
            const { width, height } = entry.contentRect;
            if (!width || !height || (width === previousWidth && height === previousHeight)) return;
            previousWidth = width;
            previousHeight = height;
            if (frame !== undefined) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(resize);
          });
          observer.observe(containerRef.current);
        }
        window.addEventListener('resize', resize);
        resize();
        setStatus('ready');
        callbacksRef.current.onReady?.();
      } catch {
        release();
        setStatus('error');
        callbacksRef.current.onError?.();
      }
    }).catch(() => {
      if (cancelled) return;
      setStatus('error');
      callbacksRef.current.onError?.();
    });

    return () => {
      cancelled = true;
      release();
    };
  }, [active, attempt, resize]);

  useEffect(() => {
    const ymaps = ymapsRef.current;
    const map = mapRef.current;
    if (!ymaps || !map || !active || status !== 'ready' || !showZones) return;
    const polygons: object[] = [];
    deliveryPoints.forEach((point) => {
      (point.zones ?? []).forEach((zone) => {
        if (!zone.polygon?.length) return;
        const convertedPolygon = zone.polygon.map((ring) =>
          ring.map((coord) => coord.length === 2 ? [coord[1], coord[0]] : coord)
        );
        try {
          const paid = Number(zone.delivery_cost) > 0;
          const polygon = new ymaps.Polygon(
            convertedPolygon,
            { hintContent: `Доставка: ${zone.delivery_cost}₽` },
            {
              fillColor: paid ? '#EC489918' : '#7B42FF22',
              strokeColor: paid ? '#EC4899' : '#7B42FF',
              strokeWidth: 1,
              strokeOpacity: 0.35,
            }
          );
          map.geoObjects.add(polygon);
          polygons.push(polygon);
        } catch { /* A malformed zone must not disable address selection. */ }
      });
    });
    return () => {
      if (mapRef.current !== map) return;
      // Remove only this effect's zones; leave the selected-address marker intact.
      polygons.forEach((polygon) => map.geoObjects.remove(polygon));
    };
  }, [active, status, attempt, showZones, deliveryPoints]);

  useEffect(() => {
    const ymaps = ymapsRef.current;
    const map = mapRef.current;
    if (!ymaps || !map || !active || status !== 'ready') return;
    try {
      if (latitude === undefined || longitude === undefined ||
        !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        if (markerRef.current) map.geoObjects.remove(markerRef.current);
        markerRef.current = null;
        return;
      }
      const center = [latitude, longitude];
      map.setCenter(center, zoom, { duration: 300 });
      if (markerRef.current) {
        markerRef.current.geometry.setCoordinates(center);
      } else {
        markerRef.current = new ymaps.Placemark(center, {}, MARKER_OPTIONS);
        map.geoObjects.add(markerRef.current);
      }
    } catch { /* A map error must not affect the checked address in the store. */ }
  }, [latitude, longitude, zoom, active, status, attempt]);

  return (
    <div
      className={cn('relative isolate w-full h-full min-h-[200px] rounded-card overflow-hidden bg-[var(--surface-secondary)]', className)}
      role="region"
      aria-label="Карта зон доставки"
      aria-busy={active && status === 'loading'}
    >
      <div ref={containerRef} className="absolute inset-0" />
      {active && status !== 'ready' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[var(--surface-secondary)] px-6 text-center">
          <p role="status" className="text-sm font-medium text-[var(--text-primary)]">
            {status === 'loading' ? 'Загружаем карту…' : 'Карта пока недоступна'}
          </p>
          {status === 'error' && (
            <>
              <p className="max-w-[260px] text-sm text-[var(--text-secondary)]">Адрес можно ввести в поле поиска.</p>
              <button
                type="button"
                className="min-h-11 rounded-full border border-[var(--border)] px-5 text-sm font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--surface-elevated)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary)]"
                onClick={(event) => { event.stopPropagation(); setStatus('loading'); setAttempt((value) => value + 1); }}
              >
                Повторить
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
});
